import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Typography, IconButton, Tooltip, Button, useMediaQuery, Snackbar, Alert, Badge, Menu, MenuItem, ListItemText } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useSocket } from '../contexts/SocketContext'
import CustomSnackbar from '../customcomponents/CustomSnackBar';
import CustomCallAlert from '../customcomponents/CustomCallAlert';
import { useToastActions } from '../customcomponents/CustomToast';
import SettingsIcon from "@mui/icons-material/Settings";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
function capitalizeFirstChar(str) {
  if (!str) return '';
  return str.replace(/\b\w/g, char => char.toUpperCase());
}

function AppHeader({ onMenuClick, mode, setMode, handleLogout, userName }) {
  const userRole=localStorage.getItem('userRole') || null;
  const isMdDown = useMediaQuery(theme => theme.breakpoints.down('md'));
  const { socket: socketContext, isConnected: isSocketConnected, on } = useSocket();
  const toast = useToastActions(6000);

  const [callAlert, setCallAlert] = useState({ open: false, data: '', severity: 'success' });
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [aiFailureNotifications, setAiFailureNotifications] = useState([]);
  const [unreadAiFailureCount, setUnreadAiFailureCount] = useState(0);
  

  const handleCallAlertClose = (event, reason) => {
    setCallAlert({ ...callAlert, open: false });
  };

  const handleOpenNotifications = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    setUnreadAiFailureCount(0);
  };

  const handleCloseNotifications = () => {
    setNotificationAnchorEl(null);
  };


  useEffect(() => {
    if (socketContext && isSocketConnected) {
      console.log('Setting up live message notification listener');
      
      socketContext.on('live-message-notification-response', (data) => {
        console.log('Live message notification response:', data);
        const name = data?.name || data?.mobile;
        toast.success(`You have a new message from ${name}`);
      });

      socketContext.on('device_data_linked', (data) => {
        console.log('Device data linked:', data);
        const messageData = {
          messageId: data?.id,
          mobile: data?.caller_number,
          name: data?.caller_name || data?.caller_number,
          success: true,
          time: data?.call_timestamp,
          type: "call",
        };
        setCallAlert({ open: true, data: {...messageData,message: "Now calling..."}, severity: 'info' });
      });

      socketContext.on('ai-auto-reply-failed', (data) => {
        const failureNotification = {
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          title: data?.title || 'AI Auto Reply Failed',
          body: data?.body || 'AI was not able to send a reply.',
          ticketId: data?.data?.ticketId || null,
          timestamp: data?.timestamp || new Date().toISOString()
        };

        setAiFailureNotifications((prev) => [failureNotification, ...prev].slice(0, 30));
        setUnreadAiFailureCount((prev) => prev + 1);
      });
    }
  }, [socketContext, isSocketConnected]);
const navigate = useNavigate();

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, background: 'linear-gradient(135deg, rgb(49, 174, 190) 0%, #0088cc 100%)' }}>
      <Toolbar>
        <IconButton color="inherit" edge="start" onClick={onMenuClick} sx={{ mr: 2 }}>
          <MenuIcon sx={{color:'background.text'}} />
        </IconButton>
        <Typography variant={isMdDown ? 'subtitle1' : 'h6'} noWrap component="div" 
          sx={{ flexGrow: 1, color: 'background.text', }} >
          SappSapp
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mr: 2, color:'background.text',fontSize:{xs: 12, sm: 16,md:16,lg:16}, display: { xs: 'none', sm: 'block' } }}>
          {capitalizeFirstChar(localStorage.getItem('name'))}
        </Typography>
        {userRole?.toLowerCase() ==='superadmin' &&
        <IconButton onClick={() => navigate("/page")} sx={{ p: 1.2, color: "background.text", }} >
          <SettingsIcon sx={{ fontSize: 26 }} />
        </IconButton>}
        <Tooltip title={mode === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'} sx={{color:'background.text'}}>
          <IconButton color="inherit" onClick={() => setMode(mode === "light" ? "dark" : "light")}>
            {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="AI notifications">
          <IconButton color="black" onClick={handleOpenNotifications}>
            <Badge badgeContent={unreadAiFailureCount} color="error" max={99}>
              <NotificationsNoneIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      {isMdDown ? (
        <IconButton
          color="error"
          onClick={handleLogout}
          sx={{
            ml: 2,
            // boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.3)',
            // bgcolor: 'rgba(255, 0, 0, 0.1)',
            '&:hover': { bgcolor: 'rgba(255, 0, 0, 0.2)' },
          }}
        >
          <LogoutIcon />
        </IconButton>
      ) : (
        <Button
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
          sx={{
            borderRadius: 3,
            fontWeight: 600,
            px: 2.5,
            py: 1,
            ml:1,
            textTransform: "none",
            backdropFilter: "blur(6px)",
            background: "rgba(255,255,255,0.15)",
            color: "white",
            fontSize: 15,
            transition: "0.3s",
            '&:hover': {
              background: "rgba(255,255,255,0.25)",
              boxShadow: "0 4px 14px rgba(0,0,0,0.2)",
            },
          }}
        >
          Logout
        </Button>

      )}
      </Toolbar>

      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleCloseNotifications}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {aiFailureNotifications.length === 0 ? (
          <MenuItem disabled>
            <ListItemText
              primary="No AI failure notifications"
              secondary="AI issue alerts will appear here"
            />
          </MenuItem>
        ) : (
          aiFailureNotifications.map((item) => (
            <MenuItem key={item.id} onClick={handleCloseNotifications}>
              <ListItemText
                primary={item.title}
                secondary={`${item.body}${item.ticketId ? ` (Ticket: ${item.ticketId})` : ''}`}
              />
            </MenuItem>
          ))
        )}
      </Menu>

      <CustomCallAlert
        open={callAlert?.open}
        onClose={handleCallAlertClose}
        onAccept={()=>{}}
        onDecline={()=>{}}
        name={callAlert?.data?.name}
        subtitle={callAlert?.data?.message}
        avatarSrc={callAlert?.data?.avatarSrc}
      />

    </AppBar>
  );
}

export default AppHeader; 